import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Factory, Shield, Award, CheckCircle, Truck, Users, Settings, ArrowRight, Zap, Globe, Package, Clock, ThumbsUp, Target, Layers, Battery, BatteryCharging } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MobileChargerManufacturer = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set Page Title for SEO
    document.title = 'Best Mobile Charger Manufacturer in Noida | Premium Quality OEM ODM | RSD Accessories Pvt Ltd';
    
    // Set Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'RSD Accessories Pvt Ltd is the best mobile charger manufacturer in Noida, India. Premium mobile charger manufacturer offering superb quality OEM ODM chargers, car chargers, fast chargers 15W to 120W. Contact: 9857300030 / 9958559161. RSD - A Brand of New Eon.';
    
    // Set Meta Keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = 'best mobile charger manufacturer in noida, premium mobile charger manufacturer in noida, mobile charger factory noida, OEM ODM charger manufacturer, car charger manufacturer noida, fast charger manufacturer india, 65W charger manufacturer, 120W charger manufacturer';
    
    // Set Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://rsdaccessories.com/best-mobile-charger-manufacturer-in-noida';
    
    // Set Open Graph Tags
    const ogTags = {
      'og:title': 'Best Mobile Charger Manufacturer in Noida | RSD Accessories Pvt Ltd',
      'og:description': 'RSD Accessories is the best mobile charger manufacturer in Noida with 3L+ chargers/month production. Premium quality OEM ODM fast chargers, car chargers.',
      'og:type': 'website',
      'og:url': 'https://rsdaccessories.com/best-mobile-charger-manufacturer-in-noida',
      'og:image': '/images/site/site_13.jpg',
      'og:site_name': 'RSD Accessories',
      'og:locale': 'en_IN'
    };
    
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    
    // Set Twitter Card Tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Best Mobile Charger Manufacturer in Noida | RSD Accessories',
      'twitter:description': 'Premium quality OEM ODM mobile charger factory with 3L+ chargers/month. Best charger manufacturer in Noida, India.',
      'twitter:image': '/images/site/site_13.jpg'
    };
    
    Object.entries(twitterTags).forEach(([name, content]) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    
    // Add Schema Markup for Local Business
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = 'charger-local-business-schema';
    schemaScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "RSD Accessories Pvt Ltd - Best Mobile Charger Manufacturer in Noida",
      "description": "RSD Accessories Pvt Ltd is the best mobile charger manufacturer in Noida, India. Premium mobile charger manufacturer with 3L+ chargers/month production capacity.",
      "url": "https://rsdaccessories.com/best-mobile-charger-manufacturer-in-noida",
      "logo": "https://rsdaccessories.com/logo.png",
      "image": "/images/site/site_13.jpg",
      "telephone": ["+91-9857300030", "+91-9958559161"],
      "email": "contact@rsdaccessories.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-08, Sector 57",
        "addressLocality": "Noida",
        "addressRegion": "Uttar Pradesh",
        "postalCode": "201301",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "28.6081",
        "longitude": "77.3649"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "19:00"
      },
      "priceRange": "₹₹",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "500"
      },
      "sameAs": [
        "https://www.facebook.com/rsdaccessories",
        "https://www.instagram.com/rsdaccessories",
        "https://www.linkedin.com/company/rsdaccessories"
      ],
      "areaServed": {
        "@type": "Country",
        "name": "India"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Mobile Chargers",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "65W Super Fast Charger",
              "category": "Mobile Chargers"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "120W Flash Charger",
              "category": "Mobile Chargers"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Car Charger QC+PD",
              "category": "Car Chargers"
            }
          }
        ]
      }
    });
    
    // Remove existing schema if present
    const existingSchema = document.getElementById('charger-local-business-schema');
    if (existingSchema) {
      existingSchema.remove();
    }
    document.head.appendChild(schemaScript);
    
    // Cleanup on unmount
    return () => {
      const schema = document.getElementById('charger-local-business-schema');
      if (schema) {
        schema.remove();
      }
    };
  }, []);

  const products = [
    {
      name: 'Silver Jack 65W Super VOOC Charger',
      description: 'Best mobile charger manufacturer in Noida brings you premium quality 65W Super VOOC fast charger with dual port technology',
      image: '/images/site/site_1.jpg',
      power: '65W'
    },
    {
      name: 'RSD 15W Fast Charger',
      description: 'Premium mobile charger manufactured in Noida with fireproof design and high temperature resistance for ultimate safety',
      image: '/images/site/site_9.png',
      power: '15W'
    },
    {
      name: '120W Flash Charger QC PD',
      description: 'Superb quality 120W flash charger - the fastest charger from the best mobile charger manufacturer in India',
      image: '/images/site/site_14.jpg',
      power: '120W'
    },
    {
      name: 'Premium QC PD Charger',
      description: 'Premium quality charger with superior build - manufactured at our state-of-the-art facility in Noida',
      image: '/images/site/site_16.jpg',
      power: 'QC 3.0'
    },
    {
      name: '65W Thunder Gold Charger',
      description: 'Best quality 65W charger with premium gold accents - OEM ODM solutions from top mobile charger manufacturer',
      image: '/images/site/site_13.jpg',
      power: '65W'
    },
    {
      name: 'Dual Port USB Charger',
      description: 'Premium dual port USB-A charger with indicator light - best quality for multi-device charging from top Noida manufacturer',
      image: '/images/site/site_19.jpg',
      power: 'Dual Port'
    },
    {
      name: '120W Premium White Charger',
      description: 'Superb quality 120W white charger with premium finish - fastest charging from the best mobile charger manufacturer',
      image: '/images/site/site_12.jpg',
      power: '120W'
    },
    {
      name: '65W Black Jack Super Fast',
      description: 'Premium 65W Super Fast charger in sleek black finish - OEM ODM solutions from best charger manufacturer in Noida',
      image: '/images/site/site_17.jpg',
      power: '65W'
    },
    {
      name: 'RSD Premium Car Charger QC+PD',
      description: 'Best car charger manufacturer in Noida - compact transparent design with dual QC+PD ports for fast in-car charging',
      image: '/images/site/site_15.jpg',
      power: 'QC+PD'
    },
    {
      name: 'Dual USB-A Fast Charger',
      description: 'Premium quality dual USB-A fast charger with LED indicator - superb build quality from best mobile charger manufacturer',
      image: '/images/site/site_20.png',
      power: '25W'
    },
    {
      name: 'Premium Car Charger',
      description: 'Best car charger manufacturer in Noida - superb quality dual port car chargers for fast on-the-go charging',
      image: '/images/site/site_46.png',
      power: '45W'
    }
  ];

  const whyChooseUs = [
    { icon: Factory, title: 'Best Mobile Charger Factory in Noida', description: 'State-of-the-art manufacturing unit in Sector 57, Noida with advanced German machinery for premium quality charger production' },
    { icon: Settings, title: 'OEM & ODM Manufacturing Excellence', description: 'Complete OEM ODM solutions for brands - custom private label manufacturing with your branding and premium housing' },
    { icon: Shield, title: 'Superb Quality Assurance', description: 'BIS certified quality control with rigorous 15-point testing for every charger - best quality guaranteed with premium components' },
    { icon: BatteryCharging, title: 'Premium Charging Technology', description: 'Support for all fast charging protocols - QC 3.0, PD, VOOC, Warp, Dart - from the best mobile charger manufacturer' },
    { icon: Award, title: 'Premium Housing & Build', description: 'Fireproof ABS housing with premium finish - reliable team ensures superb quality in every charger we manufacture' },
    { icon: Users, title: 'Trusted by 500+ Brands', description: 'Best mobile charger manufacturer serving distributors and retailers across India and 15+ countries globally' }
  ];

  const chargerTypes = [
    { name: '15W Chargers', power: 'Standard', icon: Battery },
    { name: '25W Chargers', power: 'Fast', icon: BatteryCharging },
    { name: '45W Chargers', power: 'Super Fast', icon: Zap },
    { name: '65W Chargers', power: 'Ultra Fast', icon: Zap },
    { name: '100W Chargers', power: 'Hyper Fast', icon: Zap },
    { name: '120W Chargers', power: 'Flash', icon: Zap },
    { name: 'Car Chargers', power: 'Dual Port', icon: Target },
    { name: 'GaN Chargers', power: 'Compact', icon: Layers }
  ];

  const certifications = [
    'BIS Certified',
    'ISO 9001:2015',
    'RoHS Compliant',
    'CE Certified',
    'FCC Approved',
    'Quality Tested'
  ];

  const safetyFeatures = [
    'Over Voltage Protection',
    'Over Current Protection',
    'Short Circuit Protection',
    'Over Temperature Protection',
    'Surge Protection',
    'Fireproof Housing'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              {/* Breadcrumb for SEO */}
              <nav className="mb-4 text-sm" aria-label="Breadcrumb">
                <ol className="flex items-center gap-2 text-neutral-400">
                  <li><Link to="/" className="hover:text-[#FF4D00] transition-colors">Home</Link></li>
                  <li>/</li>
                  <li className="text-[#FF4D00]">Mobile Charger Manufacturer</li>
                </ol>
              </nav>
              <div className="inline-flex items-center gap-2 bg-[#FF4D00]/20 border border-[#FF4D00]/30 rounded-full px-4 py-2 mb-6">
                <BatteryCharging className="w-4 h-4 text-[#FF4D00]" />
                <span className="text-sm text-[#FF4D00] font-medium">Best Mobile Charger Factory in India | Made in Noida</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight">
                Best <span className="text-[#FF4D00]">Mobile Charger Manufacturer</span> in Noida
              </h1>
              <p className="text-lg md:text-xl text-neutral-300 mb-4 leading-relaxed">
                <strong>RSD Accessories Pvt Ltd</strong> is the <strong>best mobile charger manufacturer in Noida</strong>, India. We are a leading <strong>premium mobile charger manufacturer</strong> with our own brand <strong>RSD - A Brand of New Eon</strong>.
              </p>
              <p className="text-base text-neutral-400 mb-8 leading-relaxed">
                We manufacture <strong>premium and superb quality mobile chargers</strong> in India. As the <strong>best mobile charger manufacturer in Noida</strong>, we specialize in <strong>OEM ODM</strong> production of fast chargers, car chargers, and all types of mobile charging solutions with <strong>premium housing</strong> and a <strong>reliable team</strong>.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+919857300030">
                  <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    Call: 9857300030
                  </Button>
                </a>
                <a href="tel:+919958559161">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    Call: 9958559161
                  </Button>
                </a>
              </div>
              <div className="mt-6">
                <Link to="/become-distributor">
                  <Button variant="link" className="text-[#FF4D00] hover:text-white px-0 text-lg">
                    Become a Distributor
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/images/site/site_13.jpg"
                alt="Best Mobile Charger Manufacturer in Noida - RSD Accessories Premium Quality OEM ODM"
                className="w-full max-w-lg mx-auto rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#FF4D00] text-white px-6 py-3 rounded-xl font-bold shadow-lg">
                Premium Quality OEM ODM
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-[#FF4D00] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            <div>
              <p className="text-4xl md:text-5xl font-black">3L+</p>
              <p className="text-sm md:text-base opacity-90">Chargers/Month</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black">500+</p>
              <p className="text-sm md:text-base opacity-90">Brand Partners</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black">30+</p>
              <p className="text-sm md:text-base opacity-90">Charger Models</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black">15+</p>
              <p className="text-sm md:text-base opacity-90">Countries Served</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Manufacturing Unit Section */}
      <section className="py-20 bg-neutral-50" id="about">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900">
                About Our <span className="text-[#FF4D00]">Premium Manufacturing Unit</span> in Noida
              </h2>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                <strong>RSD Accessories Pvt Ltd</strong> operates the <strong>best mobile charger manufacturing unit in Noida</strong>, Uttar Pradesh, India. Our state-of-the-art facility in Sector 57 is equipped with cutting-edge German and Japanese machinery that ensures consistent <strong>premium quality</strong> output for every charger we produce.
              </p>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                As the leading <strong>premium mobile charger manufacturer in Noida</strong>, we have invested heavily in advanced production lines, automated quality testing equipment, and a dedicated R&D department. Our <strong>reliable team</strong> of engineers and quality specialists work tirelessly to ensure that every charger meets international safety and performance standards.
              </p>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                We proudly own the brand <strong>RSD - A Brand of New Eon</strong>, which represents our commitment to innovation and <strong>superb quality</strong>. Our manufacturing unit produces <strong>premium housing</strong> chargers with fireproof ABS materials, gold-plated connectors, and advanced chipsets for optimal charging performance.
              </p>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                Being the <strong>best mobile charger manufacturer in Noida</strong>, we understand the importance of reliability and safety. Our <strong>OEM ODM</strong> solutions have helped hundreds of brands establish their presence in the mobile accessories market with <strong>superb quality</strong> products.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <p className="text-3xl font-black text-[#FF4D00]">3L+</p>
                  <p className="text-neutral-600">Chargers/Month</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <p className="text-3xl font-black text-[#FF4D00]">500+</p>
                  <p className="text-neutral-600">Brand Partners</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <p className="text-3xl font-black text-[#FF4D00]">30+</p>
                  <p className="text-neutral-600">Charger Models</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <p className="text-3xl font-black text-[#FF4D00]">100%</p>
                  <p className="text-neutral-600">BIS Certified</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/images/site/site_9.png"
                alt="RSD Accessories - Best Premium Mobile Charger Manufacturer in Noida India"
                className="w-full rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Charger Types Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Premium Quality <span className="text-[#FF4D00]">Charger Types</span> We Manufacture
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              As the <strong>best mobile charger manufacturer in Noida</strong>, we offer a complete range of <strong>premium and superb quality chargers</strong> for every need - from standard chargers to ultra-fast flash charging solutions.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {chargerTypes.map((charger, index) => (
              <div key={index} className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-2xl p-4 text-center text-white hover:scale-105 transition-transform">
                <charger.icon className="w-8 h-8 mx-auto mb-2 text-[#FF4D00]" />
                <h3 className="font-bold text-xs mb-1">{charger.name}</h3>
                <p className="text-xs text-neutral-400">{charger.power}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-neutral-50" id="products">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Our <span className="text-[#FF4D00]">Premium Charger Products</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              As the <strong>best mobile charger manufacturer in Noida</strong>, we produce a wide range of <strong>premium quality</strong> chargers including fast chargers, car chargers, and custom <strong>OEM ODM</strong> solutions. Our <strong>superb quality</strong> products with <strong>premium housing</strong> are trusted by leading brands across India.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div key={index} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-neutral-100">
                <div className="aspect-square overflow-hidden bg-neutral-100 relative">
                  <img 
                    src={product.image}
                    alt={`${product.name} - Best Mobile Charger Manufacturer Noida Premium Quality`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4 bg-[#FF4D00] text-white px-3 py-1 rounded-full text-sm font-bold">
                    {product.power}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">{product.name}</h3>
                  <p className="text-neutral-600">{product.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/shop?category=mobile-chargers">
              <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-full">
                View All Chargers
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Safety Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              <span className="text-[#FF4D00]">Premium Safety</span> Features in Our Chargers
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              As the <strong>best mobile charger manufacturer in Noida</strong>, safety is our top priority. Every charger comes with <strong>premium housing</strong> and multiple protection features.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {safetyFeatures.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-4 text-center text-white">
                <Shield className="w-8 h-8 mx-auto mb-2" />
                <p className="font-bold text-sm">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white" id="why-us">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why RSD is the <span className="text-[#FF4D00]">Best Mobile Charger Manufacturer</span>?
            </h2>
            <p className="text-lg text-neutral-300 max-w-3xl mx-auto">
              As the <strong>best mobile charger manufacturer in Noida</strong>, we deliver <strong>premium and superb quality</strong> products with excellence at every step. Our <strong>reliable team</strong> ensures the best <strong>OEM ODM</strong> services in India.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#FF4D00]/50 transition-all duration-300">
                <div className="w-14 h-14 bg-[#FF4D00] rounded-xl flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-neutral-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Quality <span className="text-[#FF4D00]">Certifications</span> & Standards
            </h2>
            <p className="text-lg text-neutral-600">
              Our <strong>premium quality</strong> chargers meet international standards - that's why we're the <strong>best mobile charger manufacturer in Noida</strong>
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-white rounded-full px-6 py-3 flex items-center gap-2 shadow-sm border">
                <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                <span className="font-medium text-neutral-700">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OEM ODM Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-r from-[#FF4D00] to-orange-500 rounded-3xl p-8 md:p-12 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Premium Quality OEM & ODM Charger Manufacturing
                </h2>
                <p className="text-lg mb-6 text-white/90">
                  Looking for the <strong>best mobile charger manufacturer in Noida</strong> for your brand? RSD Accessories offers complete <strong>OEM ODM</strong> solutions with <strong>premium quality</strong> and <strong>superb</strong> finish:
                </p>
                <ul className="space-y-3">
                  {[
                    'Custom branding & premium packaging design',
                    'Premium housing with fireproof ABS material',
                    'Multiple wattage options: 15W to 120W',
                    'Car charger manufacturing with dual ports',
                    'BIS certification assistance included',
                    'Dedicated account manager for OEM ODM orders',
                    'Free sampling for bulk orders',
                    'Best pricing for superb quality chargers',
                    'Reliable team support throughout production'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <img 
                  src="/images/site/site_14.jpg"
                  alt="Premium Quality OEM ODM Mobile Charger Manufacturing Noida"
                  className="w-full max-w-sm mx-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Process Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Our <span className="text-[#FF4D00]">Premium Manufacturing Process</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              As the <strong>best mobile charger manufacturer in Noida</strong>, we follow a rigorous manufacturing process with our <strong>reliable team</strong> to ensure every charger meets our <strong>superb quality</strong> standards.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Premium Component Selection', desc: 'We source only the best quality chips, capacitors, and premium housing materials for our chargers' },
              { step: '02', title: 'Precision Manufacturing', desc: 'German automated machinery ensures consistent superb quality production at our Noida facility' },
              { step: '03', title: '15-Point Quality Testing', desc: 'Every charger undergoes rigorous testing for safety, efficiency, and BIS compliance' },
              { step: '04', title: 'Premium Packaging', desc: 'Best quality packaging with custom branding options for OEM ODM partners' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100">
                <div className="text-5xl font-black text-[#FF4D00]/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-neutral-900 mb-2">{item.title}</h3>
                <p className="text-neutral-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Car Charger Section */}
      <section className="py-20 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Best <span className="text-[#FF4D00]">Car Charger Manufacturer</span> in Noida
              </h2>
              <p className="text-lg text-neutral-300 mb-6 leading-relaxed">
                RSD Accessories is also the <strong>best car charger manufacturer in Noida</strong>. We manufacture <strong>premium quality</strong> car chargers with dual ports, quick charge technology, and <strong>superb</strong> build quality for safe in-vehicle charging.
              </p>
              <p className="text-lg text-neutral-300 mb-6 leading-relaxed">
                Our <strong>reliable team</strong> designs car chargers that support all major fast charging protocols including QC 3.0, USB PD, and proprietary protocols. As the <strong>best mobile charger manufacturer in Noida</strong>, we ensure every car charger meets automotive safety standards.
              </p>
              <ul className="space-y-3 mb-8">
                {['Dual port QC + PD car chargers', 'Premium aluminum housing', 'LED indicator with smart chip', 'Universal compatibility', 'OEM ODM customization available'].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="/images/site/site_16.jpg"
                alt="Best Car Charger Manufacturer in Noida - Premium Quality"
                className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* B2B Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Benefits of Partnering with the <span className="text-[#FF4D00]">Best Charger Manufacturer</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Join 500+ brands who trust RSD Accessories as their <strong>premium mobile charger manufacturer</strong>. Experience <strong>superb quality OEM ODM</strong> services with our <strong>reliable team</strong>.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Package, title: 'Bulk Discounts', desc: 'Best pricing for premium quality chargers on large orders' },
              { icon: Clock, title: 'Fast Delivery', desc: '3L+ chargers/month capacity ensures timely delivery' },
              { icon: ThumbsUp, title: 'Quality Guarantee', desc: 'BIS certified superb quality with replacement warranty' },
              { icon: Globe, title: 'Pan-India Shipping', desc: 'Free shipping on bulk orders across India' }
            ].map((item, index) => (
              <div key={index} className="bg-neutral-50 rounded-2xl p-6 text-center border border-neutral-100">
                <item.icon className="w-12 h-12 mx-auto mb-4 text-[#FF4D00]" />
                <h3 className="text-lg font-bold mb-2 text-neutral-900">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-neutral-900 text-white" id="contact">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Contact the <span className="text-[#FF4D00]">Best Mobile Charger Manufacturer</span>
            </h2>
            <p className="text-lg text-neutral-300 max-w-3xl mx-auto">
              Get in touch with RSD Accessories Pvt Ltd - Your trusted <strong>premium mobile charger manufacturer in Noida</strong>. We are the best for all your <strong>OEM ODM</strong> charger needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <a href="tel:+919857300030" className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/10 transition-all border border-white/10">
              <div className="w-16 h-16 bg-[#FF4D00] rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Call Us</h3>
              <p className="text-[#FF4D00] text-lg font-semibold">9857300030</p>
              <p className="text-[#FF4D00] text-lg font-semibold">9958559161</p>
              <p className="text-neutral-400 mt-2">Mon-Sat: 9AM - 7PM</p>
            </a>
            
            <a href="mailto:contact@rsdaccessories.com" className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/10 transition-all border border-white/10">
              <div className="w-16 h-16 bg-[#FF4D00] rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Email Us</h3>
              <p className="text-[#FF4D00] text-lg font-semibold">contact@rsdaccessories.com</p>
              <p className="text-neutral-400 mt-2">We reply within 24 hours</p>
            </a>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/10">
              <div className="w-16 h-16 bg-[#FF4D00] rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Factory Address</h3>
              <p className="text-neutral-300">RSD Accessories Pvt Ltd</p>
              <p className="text-neutral-400 mt-2">B-08, Sector 57,<br />Noida 201301,<br />Uttar Pradesh, India</p>
            </div>
          </div>

          <div className="text-center mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/become-distributor">
              <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-full">
                Become a Distributor
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="https://wa.me/919857300030" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 px-8 py-6 text-lg rounded-full">
                WhatsApp Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Best Mobile Charger Manufacturer in Noida - RSD Accessories Pvt Ltd</h2>
            <p className="text-neutral-600 mb-4">
              <strong>RSD Accessories Pvt Ltd</strong> is proudly recognized as the <strong>best mobile charger manufacturer in Noida</strong>, Uttar Pradesh, India. Our state-of-the-art manufacturing facility in Sector 57, Noida is equipped with advanced German and Japanese machinery that enables us to produce <strong>premium and superb quality mobile chargers</strong> at scale. As the leading <strong>premium mobile charger manufacturer in Noida</strong>, we are committed to delivering excellence in every product we manufacture.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Premium Quality OEM ODM Mobile Charger Manufacturing</h3>
            <p className="text-neutral-600 mb-4">
              As the <strong>best mobile charger manufacturer in Noida</strong>, we specialize in <strong>OEM ODM</strong> manufacturing solutions for brands across India and internationally. Our <strong>reliable team</strong> produces chargers using the finest components including premium quality chips, high-grade capacitors, and <strong>premium housing</strong> with fireproof ABS materials. Being the top <strong>premium mobile charger manufacturer in Noida</strong>, we ensure every charger undergoes rigorous 15-point quality testing before shipping.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Why We Are the Best Mobile Charger Manufacturer in Noida</h3>
            <p className="text-neutral-600 mb-4">
              What makes RSD Accessories the <strong>best mobile charger manufacturer in Noida</strong> is our unwavering commitment to <strong>superb quality</strong> and customer satisfaction. Our manufacturing unit houses advanced equipment from Germany and Japan, enabling us to maintain consistent <strong>premium quality</strong> across millions of chargers. We serve as the trusted <strong>OEM ODM</strong> partner for over 500 brands with our <strong>reliable team</strong> of engineers and quality specialists.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Complete Range of Premium Mobile Chargers</h3>
            <p className="text-neutral-600 mb-4">
              Being the <strong>best mobile charger manufacturer in Noida</strong>, we produce a comprehensive range of <strong>premium quality</strong> chargers including 15W standard chargers, 25W fast chargers, 45W super fast chargers, 65W ultra fast chargers, 100W hyper fast chargers, and 120W flash chargers. Our <strong>superb quality</strong> car chargers are also manufactured with dual ports and premium aluminum housing for safe in-vehicle charging.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Best Car Charger Manufacturer in Noida</h3>
            <p className="text-neutral-600 mb-4">
              RSD Accessories is also the <strong>best car charger manufacturer in Noida</strong>. Our car chargers feature <strong>premium quality</strong> construction with dual port technology supporting QC 3.0 and USB PD fast charging. As the leading <strong>premium mobile charger manufacturer in Noida</strong>, we ensure all our car chargers meet automotive safety standards with <strong>superb</strong> build quality.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Premium Housing & Superb Quality Standards</h3>
            <p className="text-neutral-600 mb-4">
              Our chargers feature <strong>premium housing</strong> with fireproof ABS materials, gold-plated USB ports, and advanced safety chipsets. As the <strong>best mobile charger manufacturer in Noida</strong>, we provide multiple safety features including over voltage protection, over current protection, short circuit protection, over temperature protection, and surge protection. Our <strong>reliable team</strong> ensures every charger meets BIS, ISO, CE, and FCC certifications.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Contact the Best Mobile Charger Manufacturer Today</h3>
            <p className="text-neutral-600">
              Partner with <strong>RSD Accessories Pvt Ltd</strong> - the <strong>best mobile charger manufacturer in Noida</strong> for all your <strong>premium quality OEM ODM</strong> requirements. Our <strong>reliable team</strong> is ready to serve you with <strong>superb quality</strong> products. Visit our factory at <strong>B-08, Sector 57, Noida 201301</strong> or call us at <strong>9857300030 / 9958559161</strong>. Experience the difference of working with the <strong>best mobile charger manufacturer in Noida</strong> - <strong>RSD - A Brand of New Eon</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Internal Links Section for SEO */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-neutral-900 mb-8 text-center">
            Explore More from <span className="text-[#FF4D00]">RSD Accessories</span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Link to Homepage */}
            <Link to="/" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">RSD Accessories Home</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Visit our homepage to explore all products and services</p>
            </Link>
            
            {/* Link to Shop */}
            <Link to="/shop" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Shop Chargers</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Browse our complete range of premium quality chargers</p>
            </Link>
            
            {/* Link to Data Cable Page */}
            <Link to="/data-cable-manufacturer-in-noida" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Data Cable Manufacturer in Noida</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Best data cable manufacturer - Type-C, Micro USB, Lightning OEM</p>
            </Link>
            
            {/* Link to OEM Manufacturer Page */}
            <Link to="/oem-mobile-accessories-manufacturer-in-india" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">OEM Mobile Accessories Manufacturer India</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Best OEM private label mobile accessories factory in India</p>
            </Link>
            
            {/* Link to Become Distributor */}
            <Link to="/become-distributor" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Become a Distributor</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Partner with the best charger manufacturer in India</p>
            </Link>
            
            {/* Link to About Us */}
            <Link to="/about" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">About RSD Accessories</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Learn about our journey as India's best charger manufacturer</p>
            </Link>
            
            {/* Link to Contact */}
            <Link to="/contact" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Contact Us</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Get in touch for OEM ODM inquiries and bulk orders</p>
            </Link>
            
            {/* Link to Mobile Chargers Category */}
            <Link to="/shop?category=mobile-chargers" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Fast Chargers</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Premium 65W, 100W, 120W fast charging solutions</p>
            </Link>
            
            {/* Link to Warranty */}
            <Link to="/warranty-registration" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Warranty Registration</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Register your RSD products for 6-month warranty</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#FF4D00]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Partner with the Best Mobile Charger Manufacturer?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Get premium quality OEM ODM mobile chargers from the best manufacturer in Noida, India
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919857300030">
              <Button className="bg-white text-[#FF4D00] hover:bg-neutral-100 px-8 py-6 text-lg rounded-full font-bold">
                <Phone className="w-5 h-5 mr-2" />
                Call: 9857300030
              </Button>
            </a>
            <a href="tel:+919958559161">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00] px-8 py-6 text-lg rounded-full font-bold">
                <Phone className="w-5 h-5 mr-2" />
                Call: 9958559161
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MobileChargerManufacturer;
